import React, { useState, useEffect, useCallback } from 'react';
import { X, Sparkles, Download, RotateCcw, Edit3, Check, Loader2, Share, Heart, Image as ImageIcon } from 'lucide-react';
import { Recipe } from '@/types/recipe';
import { useAuth } from '@/contexts/AuthContext';
import { getCostForAction, spendCoins } from '@/services/chefcoinService';
import { useRecipeStore } from '@/stores/recipeStore';
import ChefCoinIcon from '@/components/ui/ChefCoinIcon';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';

// Import EXISTING prompt generation pipeline - CRITICAL for plan compliance
import { extractKeyVisualElements, polishPrompt } from '@/services/api/geminiApiService';
import { generateFoodPhotographyPrompt, generateImageWithVenice } from '@/services/veniceService';
import { getModelForFeature } from '@/services/modelService';

export interface ImageGenerationModalProps {
  isOpen: boolean;
  onClose: () => void;
  recipe: Recipe;
  source: 'detail-page' | 'assistant' | 'form' | 'studio';
}

interface GenerationState {
  status: 'idle' | 'generating' | 'preview' | 'editing' | 'success' | 'error';
  progress: number;
  currentStep: string;
  error?: string;
}

interface ImageData {
  url: string;
  prompt: string;
  base64?: string;
}

/**
 * NEW MODAL IMPLEMENTATION - PHASE 1: RECIPE DETAIL PAGE ONLY
 * 
 * CRITICAL REQUIREMENTS FROM PLAN:
 * 1. Use EXACT SAME prompt generation pipeline as current system
 * 2. Hide prompts by default (just like current system)
 * 3. Use REAL API calls with generateImageWithVenice()
 * 4. Mirror current recipe detail page behavior exactly
 * 5. DO NOT break assistant functionality
 */
const ImageGenerationModal: React.FC<ImageGenerationModalProps> = ({
  isOpen,
  onClose,
  recipe,
  source: _source
}) => {
  const { chefcoins_balance, setChefcoinsBalance, user, isGuest } = useAuth();
  const { updateRecipe: updateRecipeInStore } = useRecipeStore();

  // Calculate cost for image generation (same as current system)
  const cost = getCostForAction('IMAGE_GENERATE');

  const [generationState, setGenerationState] = useState<GenerationState>({
    status: 'idle',
    progress: 0,
    currentStep: ''
  });

  // CRITICAL: Hide prompt by default - matches plan requirement
  const [currentPrompt, setCurrentPrompt] = useState('');
  const [, setGeneratedImages] = useState<ImageData[]>([]);
  const [selectedImage, setSelectedImage] = useState<ImageData | null>(null);
  const [isEditingPrompt, setIsEditingPrompt] = useState(false);
  const [showPrompt, setShowPrompt] = useState(false); // HIDDEN by default per plan
  const [feedbackMessage, setFeedbackMessage] = useState<string | null>(null);

  // Auto-dismiss feedback message after 3 seconds
  useEffect(() => {
    if (feedbackMessage) {
      const timer = setTimeout(() => setFeedbackMessage(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [feedbackMessage]);

  const recipeId = recipe?.id;

  // Clear state when modal opens with new recipe
  useEffect(() => {
    if (isOpen && recipeId) {
      setGenerationState({ status: 'idle', progress: 0, currentStep: '' });
      setCurrentPrompt('');
      setGeneratedImages([]);
      setSelectedImage(null);
      setIsEditingPrompt(false);
      setShowPrompt(false); // HIDDEN by default per plan
      setFeedbackMessage(null);
    }
  }, [isOpen, recipeId]);

  /**
   * CRITICAL: Use EXACT SAME prompt generation pipeline as current system
   * This is the 4-step process described in the plan:
   * 1. extractKeyVisualElements() - AI analysis
   * 2. generateFoodPhotographyPrompt() - professional prompt creation
   * 3. polishPrompt() - natural language refinement
   * 4. generateImageWithVenice() - actual image generation
   */
  const generateProfessionalPrompt = useCallback(async () => {
    if (!recipe) {
      return `Professional food photography`;
    }

    try {
      // ========== IMAGE PROMPT DIAGNOSTICS ==========

      // Step 1: Extract key visual elements (EXISTING function)
      const keyVisualElements = await extractKeyVisualElements(recipe);

      // Step 2: Generate professional food photography prompt (EXISTING function)
      const professionalPrompt = generateFoodPhotographyPrompt(recipe, keyVisualElements);

      // Step 3: Polish the prompt using Gemini (EXISTING function)
      const finalPrompt = await polishPrompt(professionalPrompt);

      return finalPrompt;
    } catch {
      // Fallback to simple prompt
      return `Professional food photography of ${recipe.title}`;
    }
  }, [recipe]);

  /**
   * CRITICAL: Use REAL API calls with EXISTING generateImageWithVenice service
   * This mirrors the exact behavior of the current system
   */
  const generateImage = useCallback(async (customPrompt?: string, isRegenerate = false) => {
    // Check if user is guest (same as current system)
    if (isGuest()) {
      setGenerationState({
        status: 'error',
        progress: 0,
        currentStep: '',
        error: 'Please log in to generate images.'
      });
      return;
    }

    // Check ChefCoins balance (same as current system)
    if (chefcoins_balance !== null && chefcoins_balance < cost) {
      setGenerationState({
        status: 'error',
        progress: 0,
        currentStep: '',
        error: `Insufficient ChefCoins. You need ${cost} ChefCoins to generate an image.`
      });
      return;
    }

    setGenerationState({ status: 'generating', progress: 0, currentStep: 'Initializing...' });

    // Spend ChefCoins first (same pattern as current system)
    if (user?.id) {
      try {
        setGenerationState({ status: 'generating', progress: 10, currentStep: 'Processing payment...' });
        const spendResult = await spendCoins(user.id, cost, { feature: 'IMAGE_GENERATE' });
        if (!spendResult.success) {
          throw new Error(spendResult.error);
        }
        setChefcoinsBalance(spendResult.newBalance);
      } catch (error) {
        setGenerationState({
          status: 'error',
          progress: 0,
          currentStep: '',
          error: `Payment failed: ${error instanceof Error ? error.message : "Unknown error"}`
        });
        return;
      }
    }

    try {
      let finalPrompt: string;

      if (customPrompt) {
        // Use custom prompt directly
        finalPrompt = customPrompt;
        setCurrentPrompt(customPrompt);
        setGenerationState({ status: 'generating', progress: 40, currentStep: 'Using custom prompt...' });
      } else {
        // Generate prompt using EXISTING pipeline
        setGenerationState({ status: 'generating', progress: 20, currentStep: 'Generating professional prompt...' });
        finalPrompt = await generateProfessionalPrompt();
        setCurrentPrompt(finalPrompt);
        setGenerationState({ status: 'generating', progress: 40, currentStep: 'Prompt generated...' });
      }

      // Step 4: Generate image via Venice (the only provider)
      const imageModel = await getModelForFeature('imageGeneration' as any);

      let generatedImageData: string | undefined;

      setGenerationState({ status: 'generating', progress: 60, currentStep: `Creating image with Venice.ai (${imageModel})...` });

      const veniceResult = await generateImageWithVenice(recipe, finalPrompt, imageModel, !!customPrompt, isRegenerate);

      if (veniceResult.success && veniceResult.imageBase64) {
        generatedImageData = veniceResult.imageBase64;
      } else {
        throw new Error(veniceResult.error || 'Venice image generation failed');
      }

      // Create the image data object from the result
      if (generatedImageData) {
        const generatedImage: ImageData = {
          url: generatedImageData,
          prompt: finalPrompt,
          base64: generatedImageData
        };

        setGeneratedImages([generatedImage]);
        setSelectedImage(generatedImage);
        setGenerationState({ status: 'preview', progress: 100, currentStep: 'Complete!' });
      } else {
        throw new Error('No image data received from provider');
      }
    } catch (error) {

      // User-friendly error messages
      let errorMessage = "We're sorry, something went wrong while creating your image. Please try again in a moment.";
      const errorString = error instanceof Error ? error.message : String(error);

      // Venice-specific errors
      if (errorString.includes('402') || errorString.includes('Payment Required') || errorString.includes('Insufficient')) {
        errorMessage = "We're sorry, our image service is temporarily unavailable. Our team has been notified. Please try again later — we appreciate your patience! 🙏";
      }
      // Rate limiting / quota errors
      else if (errorString.includes('quota') || errorString.includes('429') || errorString.includes('rate limit')) {
        errorMessage = "We're experiencing high demand right now. Please wait a moment and try again — your image will be worth the wait! ✨";
      }
      // Network errors
      else if (errorString.includes('network') || errorString.includes('fetch') || errorString.includes('Failed to fetch') || errorString.includes('NetworkError')) {
        errorMessage = "Oops! We couldn't connect to our image service. Please check your internet connection and try again.";
      }
      // Authorization errors
      else if (errorString.includes('authorization') || errorString.includes('401') || errorString.includes('403') || errorString.includes('Unauthorized')) {
        errorMessage = "We're sorry, there's a temporary issue with our image service. Please try again shortly.";
      }
      // Timeout errors
      else if (errorString.includes('timeout') || errorString.includes('Timeout')) {
        errorMessage = "The image is taking longer than expected to generate. Please try again — sometimes the best things take a little time! 🎨";
      }
      // Model not available
      else if (errorString.includes('model') && (errorString.includes('not found') || errorString.includes('unavailable'))) {
        errorMessage = "We're sorry, the image generator is temporarily unavailable. Please try again in a few moments.";
      }
      // Server errors
      else if (errorString.includes('500') || errorString.includes('502') || errorString.includes('503') || errorString.includes('504')) {
        errorMessage = "Our image service is taking a quick break. Please try again in a moment — we'll be right back! 🔄";
      }

      setGenerationState({
        status: 'error',
        progress: 0,
        currentStep: '',
        error: errorMessage
      });
    }
  }, [recipe, chefcoins_balance, cost, user?.id, setChefcoinsBalance, isGuest, generateProfessionalPrompt]);

  const handleGenerate = useCallback(async () => {
    if (generationState.status === 'generating') return;
    await generateImage();
  }, [generationState.status, generateImage]);

  const handleRegenerate = useCallback(async () => {
    setSelectedImage(null);
    setGeneratedImages([]);
    await generateImage(undefined, true); // Pass isRegenerate = true
  }, [generateImage]);

  /**
   * CRITICAL: Apply image to recipe using EXISTING database update pattern
   * This must save the image to the recipe correctly
   */
  const handleAcceptImage = useCallback(async () => {
    if (!selectedImage) return;

    try {
      setGenerationState({ status: 'generating', progress: 80, currentStep: 'Applying image to recipe...' });

      // Update recipe with new image using the store's updateRecipe function
      await updateRecipeInStore(recipe.id, {
        image_url: selectedImage.base64 || selectedImage.url,
        has_custom_image: true
      });

      setGenerationState({ status: 'success', progress: 100, currentStep: 'Image applied to recipe!' });

      // Auto-close after success
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch {
      setGenerationState({
        status: 'error',
        progress: 0,
        currentStep: '',
        error: 'Failed to apply image to recipe'
      });
    }
  }, [selectedImage, recipe, updateRecipeInStore, onClose]);

  const handleDownload = useCallback((quality: 'standard' | 'high' = 'standard') => {
    if (!selectedImage) return;

    // Create download link with quality indicator
    const qualitySuffix = quality === 'high' ? '_high_quality' : '';
    const link = document.createElement('a');
    link.href = selectedImage.url;
    link.download = `${recipe.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}${qualitySuffix}_image.jpg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [selectedImage, recipe]);

  const handleShare = useCallback(async () => {
    if (!selectedImage) return;

    // Try to use Web Share API if available
    if (navigator.share) {
      try {
        const response = await fetch(selectedImage.url);
        const blob = await response.blob();
        const file = new File([blob], `${recipe.title}_image.jpg`, { type: 'image/jpeg' });

        await navigator.share({
          title: `AI Generated Image for ${recipe.title}`,
          text: `Check out this AI-generated image for ${recipe.title} on ChefLab!`,
          files: [file]
        });
      } catch {
        // Fallback to copying to clipboard
        navigator.clipboard.writeText(selectedImage.url);
        setFeedbackMessage('Image link copied to clipboard!');
      }
    } else {
      // Fallback for browsers without Web Share API
      navigator.clipboard.writeText(selectedImage.url);
      setFeedbackMessage('Image link copied to clipboard!');
    }
  }, [selectedImage, recipe]);

  const handleSaveToLibrary = useCallback(() => {
    if (!selectedImage) return;

    // Save to localStorage for now (could be enhanced with proper library later)
    const savedImages = JSON.parse(localStorage.getItem('cheflab_saved_images') || '[]');
    const imageToSave = {
      id: Date.now(),
      recipeId: recipe.id,
      recipeTitle: recipe.title,
      imageUrl: selectedImage.url,
      prompt: selectedImage.prompt,
      savedAt: new Date().toISOString()
    };

    savedImages.push(imageToSave);
    localStorage.setItem('cheflab_saved_images', JSON.stringify(savedImages));

    // Show success feedback
    setFeedbackMessage('Image saved to your library!');
  }, [selectedImage, recipe]);

  const handleEditPrompt = useCallback(async () => {
    // If no prompt exists, generate one first
    if (!currentPrompt) {
      try {
        const prompt = await generateProfessionalPrompt();
        setCurrentPrompt(prompt);
      } catch {
        setCurrentPrompt(`Professional food photography of ${recipe.title}`);
      }
    }
    setIsEditingPrompt(true);
    setShowPrompt(true);
  }, [currentPrompt, generateProfessionalPrompt, recipe.title]);

  const handleSavePrompt = useCallback(async () => {
    setIsEditingPrompt(false);
    setSelectedImage(null);
    setGeneratedImages([]);
    setGenerationState({ status: 'idle', progress: 0, currentStep: '' });

    // Generate image with the edited prompt
    if (currentPrompt.trim()) {
      await generateImage(currentPrompt.trim());
    }
  }, [currentPrompt, generateImage]);

  if (!isOpen || !recipe) return null;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose() }}>
      <DialogContent
        className="w-full max-w-4xl p-0 gap-0 max-h-[100dvh] sm:max-h-[90vh] overflow-hidden flex flex-col"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        {/* Header - Mobile-optimized */}
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-slate-200 shrink-0">
          <div className="flex items-center space-x-3 min-w-0">
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-r from-orange-500 to-pink-500 rounded-full flex items-center justify-center shrink-0">
              <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
            </div>
            <div className="min-w-0">
              <DialogTitle className="text-lg sm:text-xl font-semibold text-slate-900">Generate AI Image</DialogTitle>
              <p className="text-xs sm:text-sm text-slate-500 line-clamp-1">{recipe.title}</p>
            </div>
          </div>

          <button
            onClick={onClose}
            aria-label="Close"
            className="w-10 h-10 rounded-full hover:bg-slate-100 flex items-center justify-center transition-colors text-slate-600 shrink-0"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Main Content */}
        <div className="flex-1 overflow-y-auto overscroll-contain">
          <div className="p-4 sm:p-6 space-y-6">

            {/* Feedback Toast */}
            {feedbackMessage && (
              <div className="fixed top-20 left-1/2 transform -translate-x-1/2 z-[60] animate-in fade-in slide-in-from-top-2 duration-200">
                <div className="bg-green-600 text-white px-6 py-3 rounded-xl shadow-lg flex items-center gap-2">
                  <Check className="w-5 h-5" />
                  <span className="font-medium">{feedbackMessage}</span>
                </div>
              </div>
            )}

            {/* Image Display Section - Mobile-responsive comparison */}
            {recipe.image_url && (
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-slate-900">
                  {selectedImage ? 'Image Comparison' : 'Current Recipe Image'}
                </h3>

                {/* Mobile-first responsive comparison */}
                {selectedImage ? (
                  <>
                    {/* Mobile: Stacked comparison */}
                    <div className="md:hidden space-y-4">
                      {/* Current Image */}
                      <div className="space-y-2">
                        <div className="relative">
                          <img
                            src={recipe.image_url}
                            alt={`Current image for ${recipe.title}`}
                            className="w-full h-48 object-cover rounded-xl border border-slate-200"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-xl" />
                          <div className="absolute bottom-2 left-2 bg-black/70 text-white text-xs px-2 py-1 rounded-md font-medium">
                            Current Image
                          </div>
                        </div>
                        <p className="text-xs text-slate-600 text-center font-medium">
                          Your original image
                        </p>
                      </div>

                      {/* Generated Image */}
                      <div className="space-y-2">
                        <div className="relative">
                          <img
                            src={selectedImage.url}
                            alt={`Generated image for ${recipe.title}`}
                            className="w-full h-48 object-cover rounded-xl border-2 border-orange-300"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-xl" />
                          <div className="absolute bottom-2 left-2 bg-gradient-to-r from-orange-500 to-pink-500 text-white text-xs px-2 py-1 rounded-md font-medium">
                            AI Generated
                          </div>
                        </div>
                        <p className="text-xs text-slate-600 text-center font-medium">
                          New AI-generated image
                        </p>
                      </div>
                    </div>

                    {/* Desktop: Side-by-side comparison */}
                    <div className="hidden md:grid grid-cols-2 gap-4">
                      {/* Current Image - Left Side */}
                      <div className="space-y-2">
                        <div className="relative">
                          <img
                            src={recipe.image_url}
                            alt={`Current image for ${recipe.title}`}
                            className="w-full h-48 object-cover rounded-xl border border-slate-200"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-xl" />
                          <div className="absolute bottom-2 left-2 bg-black/70 text-white text-xs px-2 py-1 rounded-md font-medium">
                            Current Image
                          </div>
                        </div>
                        <p className="text-xs text-slate-600 text-center font-medium">
                          Your original image
                        </p>
                      </div>

                      {/* Generated Image - Right Side */}
                      <div className="space-y-2">
                        <div className="relative">
                          <img
                            src={selectedImage.url}
                            alt={`Generated image for ${recipe.title}`}
                            className="w-full h-48 object-cover rounded-xl border-2 border-orange-300"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-xl" />
                          <div className="absolute bottom-2 left-2 bg-gradient-to-r from-orange-500 to-pink-500 text-white text-xs px-2 py-1 rounded-md font-medium">
                            AI Generated
                          </div>
                        </div>
                        <p className="text-xs text-slate-600 text-center font-medium">
                          New AI-generated image
                        </p>
                      </div>
                    </div>
                  </>
                ) : (
                  /* Single image view when no new image generated */
                  <div className="relative">
                    <img
                      src={recipe.image_url}
                      alt={`Current image for ${recipe.title}`}
                      className="w-full h-48 md:h-64 object-cover rounded-xl border border-slate-200"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-xl" />
                    <div className="absolute bottom-3 left-3 bg-black/70 text-white text-xs px-3 py-1.5 rounded-lg font-medium">
                      Current Image
                    </div>
                  </div>
                )}

                {!selectedImage && (
                  <p className="text-sm text-slate-600 text-center">
                    Generate a new AI image to see a side-by-side comparison.
                  </p>
                )}
              </div>
            )}

            {/* No current image case - show placeholder or message */}
            {!recipe.image_url && selectedImage && (
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-slate-900">Generated Image</h3>
                <div className="relative">
                  <img
                    src={selectedImage.url}
                    alt={`Generated image for ${recipe.title}`}
                    className="w-full h-64 object-cover rounded-xl border-2 border-orange-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-xl" />
                  <div className="absolute bottom-3 left-3 bg-gradient-to-r from-orange-500 to-pink-500 text-white text-xs px-3 py-1.5 rounded-lg font-medium">
                    AI Generated Image
                  </div>
                </div>
                <p className="text-sm text-slate-600 text-center">
                  Your first AI-generated image for this recipe.
                </p>
              </div>
            )}

            {/* Prompt Section - HIDDEN by default per plan */}
            {showPrompt && (
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-slate-900">Image Prompt</h3>

                {isEditingPrompt ? (
                  <div className="space-y-3">
                    <textarea
                      value={currentPrompt}
                      onChange={(e) => setCurrentPrompt(e.target.value)}
                      className="w-full h-32 p-3 border border-slate-200 bg-white text-slate-900 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      placeholder="Describe the image you want to generate..."
                    />
                    <div className="flex space-x-2">
                      <button
                        onClick={handleSavePrompt}
                        className="px-4 py-2 bg-gradient-to-r from-orange-500 to-pink-500 hover:from-orange-600 hover:to-pink-600 text-white rounded-lg transition-colors"
                      >
                        Generate with Custom Prompt
                      </button>
                      <button
                        onClick={() => {
                          setIsEditingPrompt(false);
                          setShowPrompt(false);
                        }}
                        className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <p className="text-slate-700">{currentPrompt}</p>
                    </div>
                    <div className="flex items-center justify-between">
                      <button
                        onClick={() => setIsEditingPrompt(true)}
                        className="flex items-center space-x-2 text-sm text-slate-600 hover:text-slate-900 transition-colors"
                      >
                        <Edit3 className="w-4 h-4" />
                        <span>Edit Prompt</span>
                      </button>
                      <button
                        onClick={() => setShowPrompt(false)}
                        className="text-sm text-slate-500 hover:text-slate-700 transition-colors"
                      >
                        Hide Prompt
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Customize Prompt Button - Only show when prompt is hidden */}
            {!showPrompt && (
              <button
                onClick={handleEditPrompt}
                className="flex items-center space-x-2 text-sm text-slate-600 hover:text-slate-900 transition-colors"
              >
                <Edit3 className="w-4 h-4" />
                <span>Customize Prompt</span>
              </button>
            )}

            {/* Generation Progress */}
            {generationState.status === 'generating' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">{generationState.currentStep}</span>
                  <span className="text-sm text-slate-600">{generationState.progress}%</span>
                </div>
                <div className="w-full bg-slate-200 rounded-full h-2">
                  <div
                    className="bg-gradient-to-r from-orange-500 to-pink-500 h-2 rounded-full transition-all duration-300 ease-out"
                    style={{ width: `${generationState.progress}%` }}
                  />
                </div>
              </div>
            )}

            {/* Success State */}
            {generationState.status === 'success' && (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Check className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-lg font-medium text-slate-900 mb-2">Success!</h3>
                <p className="text-slate-600">Your AI-generated image has been applied to the recipe.</p>
              </div>
            )}

            {/* Error State - User-friendly design */}
            {generationState.status === 'error' && (
              <div className="text-center py-8 px-4">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-3xl">😔</span>
                </div>
                <h3 className="text-lg font-medium text-slate-900 mb-3">Oops! Something Went Wrong</h3>
                <p className="text-slate-600 mb-6 max-w-md mx-auto leading-relaxed">{generationState.error}</p>
                <button
                  onClick={handleGenerate}
                  className="px-6 py-3 bg-gradient-to-r from-orange-500 to-pink-500 hover:from-orange-600 hover:to-pink-600 text-white rounded-xl transition-all shadow-lg font-medium"
                >
                  ✨ Try Again
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Action Buttons - Mobile-first responsive design */}
        <div className="border-t border-slate-200 p-4 sm:p-6 shrink-0 pb-[max(env(safe-area-inset-bottom),1rem)]">
          <div className="space-y-4">
            {/* Primary Actions */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-center gap-3">
              {generationState.status === 'idle' && (
                <button
                  onClick={handleGenerate}
                  className="w-full md:w-auto font-medium bg-gradient-to-r from-orange-500 to-pink-500 hover:from-orange-600 hover:to-pink-600 focus:ring-2 focus:ring-pink-400 focus:ring-offset-2 focus:outline-none text-white rounded-xl px-6 py-4 md:py-3 flex items-center justify-center gap-3 transition-all shadow-lg min-h-[48px]"
                >
                  <Sparkles className="w-5 h-5" />
                  <span className="text-base font-semibold">Generate Image</span>
                  <div className="flex items-center gap-1 bg-black/20 text-white rounded-full px-3 py-1">
                    <ChefCoinIcon className="h-4 w-4" />
                    <span className="font-bold text-sm">{cost}</span>
                  </div>
                </button>
              )}

              {generationState.status === 'generating' && (
                <button
                  disabled
                  className="w-full md:w-auto px-6 py-4 md:py-3 bg-slate-200 text-slate-500 rounded-xl flex items-center justify-center space-x-2 min-h-[48px]"
                >
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span className="text-base font-medium">Generating...</span>
                </button>
              )}
            </div>

            {/* Secondary Actions - Preview State */}
            {generationState.status === 'preview' && (
              <div className="space-y-4">
                {/* Primary Action Row */}
                <div className="flex flex-col md:flex-row md:items-center md:justify-center gap-3">
                  <button
                    onClick={handleAcceptImage}
                    className="w-full md:w-auto font-medium bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 focus:ring-2 focus:ring-green-400 focus:ring-offset-2 focus:outline-none text-white rounded-xl px-6 py-4 flex items-center justify-center gap-3 transition-all shadow-lg min-h-[48px]"
                  >
                    <Check className="w-5 h-5" />
                    <span className="text-base font-semibold">Accept & Apply</span>
                  </button>
                </div>

                {/* Secondary Actions Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <button
                    onClick={handleRegenerate}
                    className="w-full font-medium bg-gradient-to-r from-orange-500 to-pink-500 hover:from-orange-600 hover:to-pink-600 focus:ring-2 focus:ring-pink-400 focus:ring-offset-2 focus:outline-none text-white rounded-xl px-4 py-3 flex flex-col items-center justify-center gap-2 transition-all shadow-md min-h-[44px]"
                  >
                    <RotateCcw className="w-5 h-5" />
                    <span className="text-sm font-semibold">Regenerate</span>
                    <div className="flex items-center gap-1 bg-black/20 text-white rounded-full px-2 py-0.5">
                      <ChefCoinIcon className="h-3 w-3" />
                      <span className="font-bold text-xs">{cost}</span>
                    </div>
                  </button>

                  <button
                    onClick={() => handleDownload('standard')}
                    className="w-full bg-slate-100 text-slate-700 rounded-xl px-4 py-3 flex flex-col items-center justify-center gap-2 transition-all shadow-md hover:bg-slate-200 min-h-[44px]"
                  >
                    <Download className="w-5 h-5" />
                    <span className="text-sm font-semibold">Download</span>
                  </button>

                  <button
                    onClick={() => handleDownload('high')}
                    className="w-full bg-slate-100 text-slate-700 rounded-xl px-4 py-3 flex flex-col items-center justify-center gap-2 transition-all shadow-md hover:bg-slate-200 min-h-[44px]"
                  >
                    <ImageIcon className="w-5 h-5" />
                    <span className="text-sm font-semibold">HD Quality</span>
                  </button>

                  <button
                    onClick={onClose}
                    className="w-full bg-slate-100 text-slate-700 rounded-xl px-4 py-3 flex flex-col items-center justify-center gap-2 transition-all shadow-md hover:bg-slate-200 min-h-[44px]"
                  >
                    <X className="w-5 h-5" />
                    <span className="text-sm font-semibold">Keep Original</span>
                  </button>
                </div>

                {/* Tertiary Actions */}
                <div className="flex flex-col md:flex-row md:items-center md:justify-center gap-3">
                  <button
                    onClick={handleShare}
                    className="w-full md:w-auto bg-blue-50 text-blue-700 rounded-xl px-4 py-3 flex items-center justify-center gap-2 transition-all shadow-md hover:bg-blue-100 min-h-[44px]"
                  >
                    <Share className="w-4 h-4" />
                    <span className="text-sm font-medium">Share</span>
                  </button>

                  <button
                    onClick={handleSaveToLibrary}
                    className="w-full md:w-auto bg-purple-50 text-purple-700 rounded-xl px-4 py-3 flex items-center justify-center gap-2 transition-all shadow-md hover:bg-purple-100 min-h-[44px]"
                  >
                    <Heart className="w-4 h-4" />
                    <span className="text-sm font-medium">Save to Library</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ImageGenerationModal;
